import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HalloweenDeco from "./components/HalloweenDeco";
import Home from "./pages/Home";
import { EVENT_PAGES } from "./data/events";
import { PRIX_PAGES } from "./data/prix";
import { ENTREPRISE_PAGES } from "./data/entreprise";
import { HUB_PAGES } from "./data/hubs";
import { INTENT_PAGES } from "./data/intent";
import { ALL_CITIES, EXTRA_CITIES } from "./data/site";
import { SERVICES_SEO } from "./data/services";
import { CITIES } from "./data/site";
import { track } from "./lib/track";

// Mesure des clics téléphone / email sur tout le site (page d'origine incluse)
function AnalyticsTracker() {
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (!a || !a.href) return;
      if (a.href.startsWith("tel:")) track("click_phone");
      else if (a.href.startsWith("mailto:")) track("click_email");
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  return null;
}

// Chargement différé : seule la page d'accueil est dans le bundle initial (LCP/INP mobile)
const Prestations = lazy(() => import("./pages/Prestations"));
const CityPage = lazy(() => import("./pages/CityPage"));
const Devis = lazy(() => import("./pages/Devis"));
const Contact = lazy(() => import("./pages/Contact"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const ServiceCityPage = lazy(() => import("./pages/ServiceCityPage"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Realisations = lazy(() => import("./pages/Realisations"));
const RealisationPost = lazy(() => import("./pages/RealisationPost"));
const Admin = lazy(() => import("./pages/Admin"));
const EventCityPage = lazy(() => import("./pages/EventCityPage"));
const PrixPage = lazy(() => import("./pages/PrixPage"));
const BudgetTraiteur = lazy(() => import("./pages/BudgetTraiteur"));
const EntrepriseServicePage = lazy(() => import("./pages/EntrepriseServicePage"));
const HubPage = lazy(() => import("./pages/HubPage"));
const IntentPage = lazy(() => import("./pages/IntentPage"));
const Noel = lazy(() => import("./pages/Noel"));
const NotFound = lazy(() => import("./pages/NotFound"));

function ScrollManager() {
  const { pathname } = useLocation();

  // Lenis (smooth scroll) : desktop uniquement, chargé après le premier rendu
  useEffect(() => {
    if (!window.matchMedia("(min-width: 768px)").matches) return undefined;
    let rafId;
    let lenis;
    let cancelled = false;
    const start = async () => {
      const { default: Lenis } = await import("lenis");
      if (cancelled) return;
      lenis = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
      const raf = (time) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    };
    const idleId = window.requestIdleCallback ? window.requestIdleCallback(start) : window.setTimeout(start, 1200);
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else clearTimeout(idleId);
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <div className="App">
        <AnalyticsTracker />
        <div className="noise-overlay" aria-hidden="true" />
        <BrowserRouter>
          <ScrollManager />
          <Navbar />
          <main>
            <Suspense fallback={<div className="min-h-screen bg-[#FAF8F5]" data-testid="page-loading" />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/prestations" element={<Prestations />} />
                <Route path="/menus-noel" element={<Noel />} />
                <Route path="/traiteur-lyon" element={<CityPage cityKey="lyon" />} />
                <Route path="/traiteur-valence" element={<CityPage cityKey="valence" />} />
                <Route path="/traiteur-grenoble" element={<CityPage cityKey="grenoble" />} />
                {Object.values(EXTRA_CITIES).map((c) => (
                  <Route key={c.slug} path={c.path} element={<CityPage cityKey={c.slug} cityData={c} />} />
                ))}
                <Route path="/devis" element={<Devis />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/realisations" element={<Realisations />} />
                <Route path="/realisations/:slug" element={<RealisationPost />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/plateau-repas" element={<Navigate to="/plateaux-repas" replace />} />
                <Route path="/plateau-repas/*" element={<Navigate to="/plateaux-repas" replace />} />
                <Route path="/broche-de-viande" element={<Navigate to="/brasero" replace />} />
                <Route path="/broche-de-viande/*" element={<Navigate to="/brasero" replace />} />
                <Route path="/animation-gourmandes" element={<Navigate to="/animations" replace />} />
                <Route path="/animation-gourmandes/*" element={<Navigate to="/animations" replace />} />
                <Route path="/budget-traiteur" element={<BudgetTraiteur />} />
                {PRIX_PAGES.map((p) => (
                  <Route key={p.slug} path={`/${p.slug}`} element={<PrixPage page={p} />} />
                ))}
                {ENTREPRISE_PAGES.map((p) => (
                  <Route key={p.slug} path={`/${p.slug}`} element={<EntrepriseServicePage page={p} />} />
                ))}
                {HUB_PAGES.map((p) => (
                  <Route key={p.slug} path={`/${p.slug}`} element={<HubPage page={p} />} />
                ))}
                {INTENT_PAGES.map((p) => (
                  <Route key={p.slug} path={`/${p.slug}`} element={<IntentPage page={p} />} />
                ))}
                {SERVICES_SEO.map((s) => (
                  <Route key={s.slug} path={`/${s.slug}`} element={<ServicePage service={s} />} />
                ))}
                {SERVICES_SEO.filter((s) => s.slug !== "plateaux-repas").flatMap((s) =>
                  Object.keys(CITIES).map((ck) => (
                    <Route key={`${s.slug}-${ck}`} path={`/${s.slug}-${ck}`} element={<ServiceCityPage service={s} cityKey={ck} />} />
                  ))
                )}
                {/* Anti-cannibalisation : /plateaux-repas-{ville} → page entreprise dédiée */}
                <Route path="/plateaux-repas-lyon" element={<Navigate to="/plateau-repas-entreprise-lyon" replace />} />
                <Route path="/plateaux-repas-valence" element={<Navigate to="/plateau-repas-entreprise-valence" replace />} />
                <Route path="/plateaux-repas-grenoble" element={<Navigate to="/plateau-repas-entreprise-grenoble" replace />} />
                {EVENT_PAGES.flatMap((ev) =>
                  Object.keys(ALL_CITIES)
                    .filter((ck) => ev.cities[ck])
                    .map((ck) => (
                    <Route key={`${ev.slug}-${ck}`} path={`/${ev.slug}-${ck}`} element={<EventCityPage event={ev} cityKey={ck} />} />
                  ))
                )}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <HalloweenDeco />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </MotionConfig>
  );
}

export default App;
